import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Search, UserRoundX } from "lucide-react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { LoadingState } from "../components/common/LoadingState";
import { ErrorState } from "../components/common/ErrorState";
import { PageHeader } from "../components/common/PageHeader";
import { incompleteProfilesAPI } from "../api";
import { useCampus, useSchool } from "../contexts";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PROFILE_GAPS, PROFILE_GAP_LABELS, type ProfileGap } from "../types/incompleteProfile";

const PAGE_LIMIT = 20;

const formatDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : "Never");

const initials = (firstName: string, lastName: string) =>
  `${firstName?.charAt(0) ?? ""}${lastName?.charAt(0) ?? ""}`.toUpperCase() || "?";

export function IncompleteProfilesPage() {
  const { selectedSchool } = useSchool();
  const { selectedCampus } = useCampus();
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [missing, setMissing] = useState<ProfileGap | undefined>();
  const [currentPage, setCurrentPage] = useState(1);

  const schoolId = selectedSchool?._id;
  const campusId = selectedCampus?._id;

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchTerm(searchInput.trim());
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A different school or campus is a different list.
  useEffect(() => {
    setCurrentPage(1);
  }, [schoolId, campusId]);

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ["incompleteProfiles", schoolId, campusId, missing, searchTerm, currentPage],
    queryFn: () =>
      incompleteProfilesAPI.getIncompleteProfiles({
        page: currentPage,
        limit: PAGE_LIMIT,
        search: searchTerm || undefined,
        missing,
        schoolId,
        campusId,
      }),
    placeholderData: keepPreviousData,
  });

  const toggleMissing = (gap: ProfileGap) => {
    setMissing((current) => (current === gap ? undefined : gap));
    setCurrentPage(1);
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <LoadingState message="Loading incomplete profiles..." />
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <ErrorState message="Failed to load incomplete profiles" />
      </DashboardLayout>
    );
  }

  const { users, pagination, summary } = data;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <PageHeader
          title="Incomplete Profiles"
          description="Students who haven't finished their profile. Click a card to see only that gap."
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          {PROFILE_GAPS.map((gap) => {
            const active = missing === gap;
            return (
              <button
                key={gap}
                type="button"
                onClick={() => toggleMissing(gap)}
                aria-pressed={active}
                className="text-left rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Card className={active ? "border-primary ring-1 ring-primary" : "hover:border-primary/50"}>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-medium">{PROFILE_GAP_LABELS[gap].label}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold">{summary[gap]}</div>
                    <p className="text-xs text-muted-foreground">{PROFILE_GAP_LABELS[gap].description}</p>
                  </CardContent>
                </Card>
              </button>
            );
          })}
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-5 w-5" />
              Search Students
            </CardTitle>
            <CardDescription>Search by name or email</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Input
                placeholder="Search students..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="max-w-md"
              />
              <Button variant="outline" onClick={() => setSearchInput("")}>
                Clear
              </Button>
              {missing && (
                <Button variant="ghost" onClick={() => toggleMissing(missing)}>
                  Show all gaps
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserRoundX className="h-5 w-5" />
              {missing ? PROFILE_GAP_LABELS[missing].label : "All incomplete profiles"}
            </CardTitle>
            <CardDescription>
              Showing {users.length} of {pagination.total} students
              {isFetching ? " · updating…" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>School</TableHead>
                    <TableHead>Campus</TableHead>
                    <TableHead>Missing</TableHead>
                    <TableHead>Interests</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last active</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No incomplete profiles found
                      </TableCell>
                    </TableRow>
                  ) : (
                    users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <Avatar>
                              <AvatarImage src={user.avatar_url ?? undefined} />
                              <AvatarFallback>{initials(user.first_name, user.last_name)}</AvatarFallback>
                            </Avatar>
                            <div>
                              <div className="font-medium">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-xs text-muted-foreground">{user.email}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{user.school_name || "-"}</TableCell>
                        <TableCell>{user.campus_name || "-"}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-sm">
                            {user.missing.map((gap) => (
                              <Badge key={gap} variant={gap === "not_onboarded" ? "destructive" : "secondary"}>
                                {PROFILE_GAP_LABELS[gap].label}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>{user.interest_count}</TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        <TableCell>{formatDate(user.last_active_at)}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {pagination.pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm text-muted-foreground">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage((p) => Math.min(pagination.pages, p + 1))}
                    disabled={currentPage >= pagination.pages}
                  >
                    Next
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
